import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Title, Text, Grid, Card } from '@tremor/react';
import { ArrowRight } from 'lucide-react';
import { KPICard } from '@/components/KPICard';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { api, extractErrorMessage } from '@/lib/api';

interface EtapaFunil {
  etapa: string;
  nome: string;
  usuarios: number;
  pct_primeira: number | null;
  pct_anterior: number | null;
  usuarios_cumulativo: number;
  pct_cumulativo_anterior: number | null;
}

interface AcaoRevisao {
  evento: string;
  rotulo: string;
  eventos: number;
  usuarios: number;
  sessoes: number;
}

interface Fluxo {
  sequencia: string;
  passos: number;
  sessoes: number;
  pct_sessoes_multi: number | null;
}

interface Transicao {
  origem: string;
  destino: string;
  transicoes: number;
  sessoes: number;
}

interface Pagina {
  pagina: string;
  views: number;
  usuarios: number;
  visitantes: number;
  sessoes: number;
}

interface JornadasData {
  periodo: { days: number; inicio: string; fim: string };
  funil: EtapaFunil[];
  acoes_revisao: AcaoRevisao[];
  fluxos: {
    sessoes_total: number;
    sessoes_uma_pagina: number;
    sessoes_multi_pagina: number;
    media_passos: number | null;
    top: Fluxo[];
  };
  transicoes: Transicao[];
  paginas: Pagina[];
}

const PERIODOS = [7, 30, 90];

const fmtInt = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : new Intl.NumberFormat('pt-BR').format(n);

const fmtPct = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : `${(n * 100).toFixed(1)}%`;

// Rótulos legíveis para os paths normalizados do app.
const NOMES_PAGINA: Record<string, string> = {
  '/': 'Início',
  '/signup': 'Cadastro',
  '/login': 'Login',
  '/revisao-v:id/:id': 'Revisão v2',
  '/revisao/:id': 'Revisão (antiga)',
  '/ficha/:id': 'Ficha',
  '/creditos': 'Créditos',
  '/lote': 'Lote',
  '/lote/:id': 'Lote (detalhe)',
  '/upload': 'Upload',
  '/impugnacao': 'Impugnação',
  '/impugnacao/:id': 'Impugnação (detalhe)',
  '/sobre': 'Sobre',
  '/forgot-password': 'Esqueci a senha',
  '/reset-password': 'Redefinir senha',
  '/termos': 'Termos',
  '/limitacoes': 'Limitações',
  '/privacidade': 'Privacidade',
  '/precos': 'Preços',
  outros: 'Outros',
};

const nomePagina = (p: string) => NOMES_PAGINA[p] ?? p;

// Revisão/ficha ganham destaque visual — é o foco do relatório.
const isRevisao = (p: string) => p.startsWith('/revisao') || p.startsWith('/ficha');

function Chip({ path }: { path: string }) {
  return (
    <span
      title={path}
      className={`inline-block whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium ${
        isRevisao(path)
          ? 'bg-blue-500/20 text-blue-300 ring-1 ring-blue-500/40'
          : 'bg-slate-700/60 text-slate-200'
      }`}
    >
      {nomePagina(path)}
    </span>
  );
}

function Sequencia({ seq }: { seq: string }) {
  const passos = seq.split(' → ');
  return (
    <div className="flex flex-wrap items-center gap-1">
      {passos.map((p, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <ArrowRight size={12} className="text-slate-500" />}
          {p === '…' ? <span className="text-xs text-slate-500">…</span> : <Chip path={p} />}
        </span>
      ))}
    </div>
  );
}

function Barra({ frac, cor = 'bg-blue-500' }: { frac: number; cor?: string }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded bg-slate-800">
      <div className={`h-full rounded ${cor}`} style={{ width: `${Math.max(0, Math.min(1, frac)) * 100}%` }} />
    </div>
  );
}

function Funil({ etapas }: { etapas: EtapaFunil[] }) {
  const max = etapas[0]?.usuarios || 1;
  return (
    <div className="mt-4 space-y-3">
      {etapas.map((e, i) => {
        const destaque = e.etapa === 'revisao' || e.etapa === 'abriu_edicao' || e.etapa === 'salvou_edicao';
        return (
          <div key={e.etapa}>
            <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
              <span className={destaque ? 'font-medium text-blue-300' : 'text-slate-200'}>
                {i + 1}. {e.nome}
              </span>
              <span className="flex items-baseline gap-3 tabular-nums">
                <span className="font-semibold text-white">{fmtInt(e.usuarios)}</span>
                <span className="w-14 text-right text-xs text-slate-400" title="% sobre a 1ª etapa">
                  {fmtPct(e.pct_primeira)}
                </span>
                <span
                  className="w-24 text-right text-xs text-slate-400"
                  title="% sobre a etapa anterior (contagem independente — pode passar de 100%)"
                >
                  {i === 0 ? '' : `← ${fmtPct(e.pct_anterior)}`}
                </span>
              </span>
            </div>
            <div className="relative h-6 w-full overflow-hidden rounded bg-slate-800">
              <div
                className={`absolute inset-y-0 left-0 rounded ${destaque ? 'bg-blue-500' : 'bg-blue-500/60'}`}
                style={{ width: `${(e.usuarios / max) * 100}%` }}
              />
              <div
                className="absolute inset-y-0 left-0 border-r-2 border-amber-400"
                style={{ width: `${(e.usuarios_cumulativo / max) * 100}%` }}
                title={`${fmtInt(e.usuarios_cumulativo)} fizeram esta etapa E todas as anteriores`}
              />
            </div>
            {i > 0 && (
              <Text className="mt-0.5 text-xs text-slate-500">
                {fmtInt(e.usuarios_cumulativo)} cumpriram esta e todas as anteriores
                {e.pct_cumulativo_anterior !== null && ` (${fmtPct(e.pct_cumulativo_anterior)} da etapa anterior)`}
              </Text>
            )}
          </div>
        );
      })}
      <Text className="pt-2 text-xs text-slate-500">
        Barra = usuários logados distintos que fizeram a etapa no período (qualquer ordem) · marca âmbar =
        quem fez a etapa e todas as anteriores · “←” = % sobre a etapa anterior.
      </Text>
    </div>
  );
}

export function JornadasPage() {
  const [days, setDays] = useState(30);

  const { data, isLoading, error } = useQuery<JornadasData>({
    queryKey: ['jornadas.dashboard', days],
    queryFn: async () => (await api().get('/jornadas/dashboard', { params: { days } })).data,
    refetchInterval: 5 * 60_000,
  });

  const errMsg = error ? extractErrorMessage(error, 'erro ao carregar jornadas') : null;
  const d = data;
  const sec = 'mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500';

  const etapa = (k: string) => d?.funil.find((e) => e.etapa === k);
  const ativos = etapa('ativo')?.usuarios ?? 0;
  const revisao = etapa('revisao');
  const salvou = etapa('salvou_edicao');
  const maxAcao = Math.max(1, ...(d?.acoes_revisao ?? []).map((a) => a.usuarios));
  const maxFluxo = Math.max(1, ...(d?.fluxos.top ?? []).map((f) => f.sessoes));
  const maxTrans = Math.max(1, ...(d?.transicoes ?? []).map((t) => t.transicoes));
  const maxPag = Math.max(1, ...(d?.paginas ?? []).map((p) => p.views));

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <div className="flex items-center justify-between">
          <div>
            <Title>Jornadas</Title>
            <Text>Fluxos típicos de navegação no app e funil até a revisão/edição de dados</Text>
          </div>
          <div className="flex items-center gap-1 rounded-md border border-slate-700 p-0.5">
            {PERIODOS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setDays(p)}
                className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                  days === p ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                {p}d
              </button>
            ))}
          </div>
        </div>
      </header>

      {errMsg && (
        <Card>
          <Text className="text-red-400">{errMsg}</Text>
        </Card>
      )}

      <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
        <KPICard label={`Usuários ativos ${days}d`} value={fmtInt(d ? ativos : null)} hint="Logados com ao menos 1 evento" loading={isLoading} />
        <KPICard
          label="Chegaram à revisão"
          value={fmtPct(revisao?.pct_primeira)}
          hint={revisao ? `${fmtInt(revisao.usuarios)} usuários viram /revisao` : undefined}
          loading={isLoading}
        />
        <KPICard
          label="Salvaram edição"
          value={fmtPct(salvou && revisao ? (revisao.usuarios ? salvou.usuarios / revisao.usuarios : null) : null)}
          hint={salvou ? `${fmtInt(salvou.usuarios)} usuários · % de quem viu a revisão` : undefined}
          loading={isLoading}
        />
        <KPICard
          label="Sessões logadas"
          value={fmtInt(d?.fluxos.sessoes_total)}
          hint={
            d
              ? `${fmtInt(d.fluxos.sessoes_multi_pagina)} com 2+ páginas · média ${d.fluxos.media_passos?.toFixed(1) ?? '—'} passos`
              : undefined
          }
          loading={isLoading}
        />
      </Grid>

      <Grid numItems={1} numItemsLg={2} className="gap-4">
        <Card>
          <Title>Funil até a revisão</Title>
          <Text>Usuários logados distintos por etapa</Text>
          {d ? <Funil etapas={d.funil} /> : <LoadingSkeleton rows={8} />}
        </Card>

        <Card>
          <Title>O que fazem na revisão</Title>
          <Text>Eventos de feature na revisão/edição/ficha — usuários distintos</Text>
          {d ? (
            <div className="mt-4 space-y-2">
              {d.acoes_revisao.map((a) => (
                <div key={a.evento} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1">
                  <span className="truncate text-sm text-slate-200" title={a.evento}>
                    {a.rotulo}
                  </span>
                  <span className="text-right text-sm tabular-nums">
                    <span className="font-semibold text-white">{fmtInt(a.usuarios)}</span>
                    <span className="ml-2 text-xs text-slate-500">{fmtInt(a.eventos)} ev.</span>
                  </span>
                  <div className="col-span-2">
                    <Barra frac={a.usuarios / maxAcao} cor={a.usuarios ? 'bg-emerald-500' : 'bg-slate-700'} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <LoadingSkeleton rows={8} />
          )}
        </Card>
      </Grid>

      <Card>
        <Title>Fluxos típicos</Title>
        <Text>
          Sequência de páginas por sessão logada (repetições consecutivas colapsadas, até 8 passos) · só sessões
          com 2+ páginas
          {d && ` — ${fmtInt(d.fluxos.sessoes_uma_pagina)} sessões de uma página só ficaram de fora`}
        </Text>
        {d ? (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-700 text-slate-400">
                  <th className="pb-2 font-medium">Sequência</th>
                  <th className="w-20 pb-2 text-right font-medium">Sessões</th>
                  <th className="w-20 pb-2 text-right font-medium">% multi</th>
                  <th className="w-40 pb-2 pl-4 font-medium" />
                </tr>
              </thead>
              <tbody>
                {d.fluxos.top.map((f) => (
                  <tr key={f.sequencia} className="border-b border-slate-800">
                    <td className="py-2 pr-2">
                      <Sequencia seq={f.sequencia} />
                    </td>
                    <td className="py-2 text-right tabular-nums">{fmtInt(f.sessoes)}</td>
                    <td className="py-2 text-right tabular-nums text-slate-400">{fmtPct(f.pct_sessoes_multi)}</td>
                    <td className="py-2 pl-4">
                      <Barra frac={f.sessoes / maxFluxo} />
                    </td>
                  </tr>
                ))}
                {d.fluxos.top.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-4 text-center text-slate-500">
                      Sem sessões com 2+ páginas no período
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <LoadingSkeleton rows={8} />
        )}
      </Card>

      <Grid numItems={1} numItemsLg={2} className="gap-4">
        <Card>
          <Title>Principais transições</Title>
          <Text>Pares origem → destino consecutivos na mesma sessão</Text>
          {d ? (
            <div className="mt-4 space-y-2">
              {d.transicoes.map((t) => (
                <div
                  key={`${t.origem}>${t.destino}`}
                  className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1"
                >
                  <div className="flex flex-wrap items-center gap-1">
                    <Chip path={t.origem} />
                    <ArrowRight size={12} className="text-slate-500" />
                    <Chip path={t.destino} />
                  </div>
                  <span className="text-right text-sm tabular-nums">
                    <span className="font-semibold text-white">{fmtInt(t.transicoes)}</span>
                    <span className="ml-2 text-xs text-slate-500">{fmtInt(t.sessoes)} sess.</span>
                  </span>
                  <div className="col-span-2">
                    <Barra frac={t.transicoes / maxTrans} cor="bg-violet-500" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <LoadingSkeleton rows={8} />
          )}
        </Card>

        <Card>
          <Title>Páginas mais vistas</Title>
          <Text>Todos os page_views (inclui anônimos) · usuários = logados distintos</Text>
          {d ? (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-700 text-slate-400">
                    <th className="pb-2 font-medium">Página</th>
                    <th className="pb-2 text-right font-medium">Views</th>
                    <th className="pb-2 text-right font-medium">Usuários</th>
                    <th className="pb-2 text-right font-medium">Visitantes</th>
                    <th className="w-24 pb-2 pl-3 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {d.paginas.map((p) => (
                    <tr key={p.pagina} className="border-b border-slate-800">
                      <td className="py-2">
                        <Chip path={p.pagina} />
                        <span className="ml-2 text-xs text-slate-500">{p.pagina !== 'outros' ? p.pagina : ''}</span>
                      </td>
                      <td className="py-2 text-right tabular-nums">{fmtInt(p.views)}</td>
                      <td className="py-2 text-right tabular-nums">{fmtInt(p.usuarios)}</td>
                      <td className="py-2 text-right tabular-nums text-slate-400">{fmtInt(p.visitantes)}</td>
                      <td className="py-2 pl-3">
                        <Barra frac={p.views / maxPag} cor="bg-slate-500" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <LoadingSkeleton rows={8} />
          )}
        </Card>
      </Grid>
    </div>
  );
}
